import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  uuid,
  commonUtils,
  useNotifications,
  useEditorStore,
  useFlowBuilder2AddFlowStepMutation,
  makeEditorNavStore,
} from '@diana/core';
import { FLOW_BUILDER2_PAGE_NS } from '../i18n';
import { scrollToStep } from './useBuilderScroll';
import { useFlowBuilder2PageStore } from '../Store/useFlowBuilder2PageStore';

export const useAddStep = (options?: Parameters<typeof useFlowBuilder2AddFlowStepMutation>[0]) => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const notifications = useNotifications();

  const setActiveTaskId = useFlowBuilder2PageStore((s) => s.setActiveTaskId);

  const getFlow = useEditorStore((s) => s.getFlow);
  const updateFlow = useEditorStore((s) => s.updateFlow);
  const flowId = useEditorStore((s) => s.flow?.id ?? '');

  const useEditorNavStore = makeEditorNavStore(flowId);
  const updateLayoutNav = useEditorNavStore((s) => s.updateLayoutNav);

  const { mutateAsync, isPending: isAddingStep } = useFlowBuilder2AddFlowStepMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      updateFlow((flow) => {
        const prevIds = new Set(flow.steps.map((s) => s.id));
        const newItem = data.find((s) => !prevIds.has(s.id));

        if (newItem) {
          updateLayoutNav({ itemId: newItem.stepId, value: true, level: 0 });
          setActiveTaskId(newItem.taskId);
          setTimeout(() => scrollToStep(newItem.stepId));
        }

        return { ...flow, steps: data };
      });
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      notifications.error({
        message: t('errors.addStepFailed'),
        description: commonUtils.getErrorMessage(error),
      });
      options?.onError?.(error, variables, context);
    },
  });

  const addStep = useCallback(async () => {
    const flow = getFlow();
    if (!flow) throw new Error('Flow is missing');

    return mutateAsync({
      flowId: flow.id,
      position: -1,
      data: {
        stepName: `NoCodeStep-${uuid()}`,
        taskName: `NoCodeTask-${uuid()}`,
        actionSetName: `NoCodeActionSet-${uuid()}`,
      },
    });
  }, [getFlow, mutateAsync]);

  return { addStep, isAddingStep };
};
